/**************************************************/
/* Methods for PCR and PCVPLS based on wasm code  */
/**************************************************/

import { ismatrix, Matrix, Vector, Index, crossprod } from 'mdatools/arrays';
import { scale as prep_scale } from 'mdatools/prep';
import { pca_getreslength, pca_getmodellength, pcamodel_get, pcares_get} from './pcvpca.js';
import { get_regstat } from './misc.js';


function get_yscores(res, model) {

   if (!res.Yref || !ismatrix(res.Yref)) return null;

   const Y = res.Yref;
   const ncols = model.ncols;
   const nresp = Y.ncols;
   const nrows = Y.nrows;
   const ncomp = res.ncomp;

   // autoscale Y
   const mY = new Vector(plsmodel_get(model, 'mY'));
   const sY = new Vector(plsmodel_get(model, 'sY'));
   const Yp = prep_scale(Y, mY, sY);

   // get y-loadings
   const c_offset = pca_getmodellength(ncols, ncomp) + ncomp * ncols;
   const c_length = ncomp * nresp;
   const C = new Matrix(model.v.subarray(c_offset, c_offset + c_length), nresp, ncomp);

   // get x-scores
   const t_offset = 0;
   const t_length = ncomp * nrows;
   const T = new Matrix(res.v.subarray(t_offset, t_offset + t_length), nrows, ncomp);

   // compute y-scores, orthogonalize and return
   const U = Yp.dot(C);

   for (let a = 2; a <= ncomp; a++) {
      const ua = U.subset([], a);
      const Ta = T.subset([], Index.seq(1, a - 1));
      const uao = ua.subtract(Ta.dot(crossprod(Ta, ua)));
      U.v.set(uao.v, (a - 1) * nrows);
   }

   return U;
}


/**
 * Returns length of array with PLS results.
 *
 * @param {number} nrows number of rows in a dataset.
 * @param {number} nresp number of response variables.
 * @param {number} ncomp number of components in the model.
 *
 * @returns {number} the length of the results array.
 */
export function pls_getreslength(nrows, nresp, ncomp) {

   // Structure of PLS results - same as PCA with extra fields at the end:
   // PCA part:
   // - T: nr x ncomp
   // - H: nr x ncomp
   // - Q: nr x ncomp
   // - expvar: 1 x ncomp
   // Extra part:
   // - Yp: nr x ny x ncomp
   // - U: nr x ncomp

   return pca_getreslength(nrows, ncomp) + nrows * nresp * ncomp + nrows * ncomp;
}


/**
 * Returns length of array with PLS model.
 *
 * @param {number} ncols number of predictors.
 * @param {number} nresp number of responses.
 * @param {number} ncomp number of components
 *
 * @returns {number} the length of the model array.
 */
export function pls_getmodellength(ncols, nresp, ncomp) {

   // Structure of PLS model - same as PCA with extra fields at the end:
   // PCA part:
   // - mX: 1 x ncols
   // - sX: 1 x ncols
   // - P: ncols x ncomp
   // - eigenvals: 1 x ncomp
   // - Q_mean: 1 x ncomp
   // - Q_sd: 1 x ncomp
   // - Q_median: 1 x ncomp
   // - Q_iqr: 1 x ncomp
   // - H_mean: 1 x ncomp
   // - H_sd: 1 x ncomp
   // - H_median: 1 x ncomp
   // - H_iqr: 1 x ncomp
   // Extra part:
   // - R: nc x ncomp
   // - C: ny x ncomp
   // - mY: ny
   // - sY: ny


   return pca_getmodellength(ncols, ncomp) + ncols * ncomp + nresp * ncomp + 2 * nresp;
}


/**
 * Returns part of PLS model array for given outcome.
 *
 * @param {TypedArray} m array with PLS model.
 * @param {string} name name of the outcome (e.g. 'yloadings', 'mY', 'sY', etc.).
 * @param {number} a component number (for loadings).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function plsmodel_get(m, name, a) {

   if (!m || !m.class || !m.class.includes('plsmodel')) {
      throw new Error('plsmodel_get: parameter "m" must be JSON with PLS model.');
   }

   const ncols = m.ncols;
   const nresp = m.nresp;
   const ncomp = m.ncomp;
   let length;
   let offset = pca_getmodellength(ncols, ncomp);

   switch(name) {
      case 'weights':
         offset = offset + (a - 1) * ncols;
         length = ncols;
         break;
      case 'yloadings':
         offset = offset + ncols * ncomp + (a - 1) * nresp;
         length = nresp;
         break;
      case 'mY':
         offset = offset + ncols * ncomp + nresp * ncomp;
         length = nresp;
         break;
      case 'sY':
         offset = offset + + ncols * ncomp + nresp * ncomp + nresp;
         length = nresp;
         break;
      default:
         return pcamodel_get(m, name, a);
   }

   return m.v.subarray(offset, offset + length);
}


/**
 * Returns part of PLS result array for given outcome.
 *
 * @param {TypedArray} r array with PLS results.
 * @param {string} name name of the outcome (e.g. 'Y', 'Yp', 'RMSE', 'R2').
 * @param {number} a component number (for scores, predictions and distances).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function plsres_get(r, name, a, ny) {

   if (!r || !r.class || !r.class.includes('plsres')) {
      throw new Error('plsres_get: parameter "r" must be JSON with PLS results.');
   }

   if (!ny) {
      ny = 1;
   }

   const nrows = r.nrows;
   const ncomp = r.ncomp;
   const nresp = r.nresp;
   let length;
   let offset = pca_getreslength(nrows, ncomp);

   switch(name) {
      case 'Yp':
         offset = offset + (a - 1) * nresp * nrows + (ny - 1) * nrows;
         length = nrows;
         break;
      case 'yscores':
         return r.U ? r.U.v.subarray((a - 1) * nrows, a * nrows) : null;
      default:
         return pcares_get(r, name, a);
   }

   return r.v.subarray(offset, offset + length);
}


/**
 * Creates a PLS model using wasm implementation.
 *
 * @param {Object} Module object with wasm module for PCV.
 * @param {Matrix} X matrix with predictors for calibration set.
 * @param {Matrix} Y matrix with responses calibration set (1 column).
 * @param {number} ncomp number of components.
 * @param {boolean} center mean center the data columns or not.
 * @param {boolean} scale standardize the data columns or not.
 *
 * @returns {Object} JSON with PLS model.
 */
export function pls_fit(Module, X, Y, ncomp, center, scale) {

   if (ncomp === undefined) {
      ncomp = Math.min(X.nrows - 1, X.ncols);
   }

   if (center === undefined) {
      center = true;
   }

   if (scale === undefined) {
      scale = false;
   }

   if (!ismatrix(X)) {
      throw new Error('pls_fit: X must be a matrix.');
   }

   if (!ismatrix(Y)) {
      throw new Error('pls_fit: Y must be a matrix.');
   }

   if (X.nrows < 3 || X.ncols < 2) {
      throw new Error('pls_fit: X must have at least 3 rows and 2 columns.');
   }

   if (X.nrows !== Y.nrows ) {
      throw new Error('pls_fit: X and Y must have the same number of rows.');
   }

   if (ncomp < 1 || ncomp > Math.min(X.nrows - 1, X.ncols)) {
      throw new Error('pls_fit: the value of "ncomp" does not match the dimension of X.');
   }

   // allocate memory in WASM heap space and copy predictors values
   const x_ptr = Module._malloc(X.v.length * X.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(X.v, x_ptr / X.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space and copy responses values
   const y_ptr = Module._malloc(Y.v.length * Y.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(Y.v, y_ptr / Y.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space for model and calibration set results outcomes
   const m_length = pls_getmodellength(X.ncols, Y.ncols, ncomp);
   const m_constructor = Float64Array;
   const m_ptr = Module._malloc(m_length * m_constructor.BYTES_PER_ELEMENT);

   // call WASM function for PCA
   const res = Module.ccall('plsfit_wasm',
               ['number'], // return type
               ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
               [x_ptr, y_ptr, X.nrows, X.ncols, Y.ncols, ncomp, center, scale, m_ptr]); // arguments

   if (res !== 1) {
      throw new Error('pls_fit: unexpected error.');
   }

   // copy part of HEAP memory with model outcomes
   const model = Module.HEAPF64.slice(
      m_ptr/m_constructor.BYTES_PER_ELEMENT,
      m_ptr/m_constructor.BYTES_PER_ELEMENT + m_length
   );


   // free the memory
   Module._free(x_ptr);
   Module._free(y_ptr);
   Module._free(m_ptr);

   // return PLS model object (can be also treated as PCA model)
   return {
      class: ['plsmodel', 'pcamodel'],
      v: model,
      ncomp: ncomp,
      nrows: X.nrows,
      ncols: X.ncols,
      nresp: Y.ncols,
      center: center,
      scale: scale
   };
}


/**
 * Projects dataset to a PLS model.
 *
 * @param {Object} Module object with wasm module for PCV.
 * @param {Object} m JSON with PLS model created by `pls_fit()`.
 * @param {Matrix} X matrix with dataset predictors.
 * @param {Matrix} Y matrix with dataset responses (optional).
 *
 * @returns {Object} JSON with PLS results.
 */
export function pls_predict(Module, m, X, Y, ncomp) {

   if (!m || !m.class || !m.class.includes('plsmodel')) {
      throw new Error('pls_predict: parameter "m" must be JSON with PLS results.');
   }

   if (!ismatrix(X)) {
      throw new Error('pls_predict: X must be a matrix.');
   }

   if (X.nrows < 1) {
      throw new Error('pls_predict X must have at least 1 row.');
   }

   if (X.ncols !== m.ncols) {
      throw new Error('pls_predict: X must have the same number of columns as the training set.');
   }

   if (!ncomp) {
      ncomp = m.ncomp;
   }

   if (ncomp > m.ncomp) {
      throw new Error('pls_predict: number of components for prediction must not exceed number of components in the model.');
   }

   // allocate memory in WASM heap space and copy dataset values
   const x_ptr = Module._malloc(X.v.length * X.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(X.v, x_ptr / X.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space and copy model values
   const m_ptr = Module._malloc(m.v.length * m.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(m.v, m_ptr / m.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space for result values
   const r_length = pls_getreslength(X.nrows, m.nresp, ncomp);
   const r_constructor = Float64Array;
   const r_ptr = Module._malloc(r_length * r_constructor.BYTES_PER_ELEMENT);

   // call WASM function for PCA prediction
   const res = Module.ccall('plspredict_wasm',
               ['number'], // return type
               ['number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
               [x_ptr, X.nrows, X.ncols, m.nresp, ncomp, m_ptr, r_ptr]); // arguments

   if (res !== 1) {
      throw new Error('pls_predict: unexpected error.');
   }

   // copy part of HEAP memory with result values
   const res_values = Module.HEAPF64.slice(
      r_ptr/r_constructor.BYTES_PER_ELEMENT,
      r_ptr/r_constructor.BYTES_PER_ELEMENT + r_length
   );

   // free the memory
   Module._free(x_ptr);
   Module._free(m_ptr);
   Module._free(r_ptr);

   // return PLS results (can be also treated as PCA results)
   const out = {
      class: ['plsres', 'pcares'],
      v: res_values,
      ncomp: ncomp,
      nrows: X.nrows,
      ncols: X.ncols,
      nresp: m.nresp,
      center: m.center,
      scale: m.scale,
      Yref: Y,
      U: null,
      stat: null
   }

   if (Y && ismatrix(Y)) {
      out.U = get_yscores(out, m);
      out.stat = get_regstat(out);
   }

   return out;
}


/**
 * Create a PV-set based on PLS decomposition.
 *
 * @param {Object} Module object with wasm module for PCV.
 * @param {Object} m JSON with PLS model created by `pcr_fit()`.
 * @param {Matrix} X matrix with predictors for calibration set.
 * @param {Matrix} Y matrix with responses calibration set (1 column).
 * @param {number} ncomp number of components for PV-set generation.
 * @param {number} cv_nseg number of segments for cross-validation.
 * @param {number} cv_type type of cross-validation (0: loo, 1: ven, 2: rand).
 * @param {number} cv_scope scope of cross-validation (0: global, 1: local).
 *
 * @returns {Array} array with two matrices: the generated PV-set and matrix of scalars D.
 */
export function pcvpls(Module, m, X, Y, ncomp, cv_nseg, cv_type, cv_scope) {

   if (!m || !m.class || !m.class.includes('plsmodel')) {
      throw new Error('pcvpls: parameter "m" must be JSON with PLS model.');
   }

   if (!ismatrix(X)) {
      throw new Error('pcvpls: X must be a matrix.');
   }

   if (X.nrows < 3) {
      throw new Error('pcvpls: X must have at least 3 rows.');
   }

   if (X.ncols !== m.ncols) {
      throw new Error('pcvpls: X must have the same number of columns as the training set.');
   }

   if (!Number.isInteger(cv_type) || ![0, 1, 2].includes(cv_type)) {
      throw new Error('pcvpls: parameter "cv_type" must be one of 0 ("loo"), 1 ("ven"), 2 ("rand).');
   }

   if (!Number.isInteger(cv_scope) || ![0, 1].includes(cv_scope)) {
      throw new Error('pcvpls: parameter "cv_scope" must be 0 ("global") or 1 ("local").');
   }

   if (cv_type === 0) {
      cv_nseg = X.nrows;
   }

   if (cv_type > 0 && (!Number.isInteger(cv_nseg) || cv_nseg < 2)) {
      throw new Error('pcvpls: parameter "cv_nseg" must be a number ≥ 2.');
   }

   if (cv_type > 0 && cv_nseg > X.nrows) {
      throw new Error('pcvpls: the value of "cv_nseg" is too large for the given X.');
   }

   if (!Number.isInteger(ncomp) || ncomp < 1) {
      throw new Error('pcvpls: the value of "ncomp" must be a number >= 1.');
   }

   if (ncomp > m.ncomp) {
      throw new Error('pcvpls: the value of "ncomp" must not exceed number of LVs in the global model.');
   }

   if (X.nrows !== Y.nrows ) {
      throw new Error('pcvpls: X and Y must have the same number of rows.');
   }

   if (Y.ncols !== 1) {
      throw new Error('pcvpls: Y must have one column.');
   }

   const max_ncomp = cv_type === 0 ?
      Math.min(X.nrows - 2, X.ncols, ncomp) :
      Math.min(X.nrows - Math.ceil(X.nrows / cv_nseg) - 1, X.ncols, ncomp);

   if (ncomp > max_ncomp) {
      throw new Error('pcvpls: the value of "ncomp" is too large.');
   }

   // allocate memory in WASM heap space and copy predictors values
   const x_ptr = Module._malloc(X.v.length * X.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(X.v, x_ptr / X.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space and copy response values
   const y_ptr = Module._malloc(Y.v.length * Y.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(Y.v, y_ptr / Y.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space and copy model values
   const m_ptr = Module._malloc(m.v.length * m.v.BYTES_PER_ELEMENT);
   Module.HEAPF64.set(m.v, m_ptr / m.v.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space for Xpv values
   const xpv_length = X.v.length;
   const xpv_constructor = X.v.constructor;
   const xpv_ptr = Module._malloc(xpv_length * xpv_constructor.BYTES_PER_ELEMENT);

   // allocate memory in WASM heap space for D values
   const d_length = cv_nseg * ncomp;
   const d_constructor = X.v.constructor;
   const d_ptr = Module._malloc(d_length * d_constructor.BYTES_PER_ELEMENT);

   // call WASM function for PCVPLS
   const res = Module.ccall('pcvpls_wasm',
               ['number'], // return type
               ['number', 'number', 'number', 'number', 'number', 'number', 'number',
               'number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
               [x_ptr, y_ptr, X.nrows, X.ncols, Y.ncols, ncomp, m.center, m.scale,
               cv_type, cv_nseg, cv_scope, m_ptr, xpv_ptr, d_ptr]); // arguments

   if (res !== 1) {
      throw new Error('pcvpls: unexpected error.');
   }

   // copy part of HEAP memory with Xpv values
   const xpv = Module.HEAPF64.slice(
      xpv_ptr/xpv_constructor.BYTES_PER_ELEMENT,
      xpv_ptr/xpv_constructor.BYTES_PER_ELEMENT + xpv_length
   );

   // copy part of HEAP memory with D values
   const d = Module.HEAPF64.slice(
      d_ptr/d_constructor.BYTES_PER_ELEMENT,
      d_ptr/d_constructor.BYTES_PER_ELEMENT + d_length
   );

   // free the memory
   Module._free(x_ptr);
   Module._free(y_ptr);
   Module._free(m_ptr);
   Module._free(xpv_ptr);
   Module._free(d_ptr);

   // return Xpv
   return [new Matrix(xpv, X.nrows, X.ncols), new Matrix(d, cv_nseg, ncomp)];
}
